"use client";

import { Trophy } from "lucide-react";
import { useTranslations } from "next-intl";
import { memo, useEffect, useMemo, useRef } from "react";

import { Link } from "@/i18n/navigation";
import type { FriendListItem } from "@/shared/api/friends";
import { Button } from "@/shared/ui/buttons/button";
import { Tournament } from "@/entities/tournament/model/types";
import { TournamentCard } from "./tournament-card";
import { TournamentCardSkeleton } from "@/entities/tournament/ui/tournament-card-skeleton";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useIsMobile } from "@/shared/lib/hooks/use-media-query";
import { withErrorBoundary } from "@/shared/lib/error-boundary";
import { TournamentInfoRow } from "@/widgets/shared/tournament-info-row";

import { ViewMode } from "./tournament-filters";

interface TournamentListProps {
  tournaments: Tournament[];
  isLoading: boolean;
  viewMode: ViewMode;
  onLoadMore?: (() => void) | undefined;
  hasMore?: boolean | undefined;
  tournamentFriendsMap?: Map<string, { friends: FriendListItem[]; count: number }>;
  /** true — если активны поиск/фильтр (тогда empty-state «измените фильтры», иначе — приглашение создать турнир). */
  filtered?: boolean;
}

const TournamentCardMemo = memo(TournamentCard);
const TournamentInfoRowMemo = memo(TournamentInfoRow);
const TournamentCardSkeletonMemo = memo(TournamentCardSkeleton);

const EmptyState = memo(({ filtered }: { filtered?: boolean }) => {
  const tUi = useTranslations("Ui");
  return (
  <div className="text-center py-12">
    <Trophy className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
    {filtered ? (
      <>
        <h3 className="text-xl font-semibold mb-2">{tUi("tournamentListOptimized.nichegoNeNaydeno")}</h3>
        <p className="text-muted-foreground">
          {tUi("tournamentListOptimized.poprobuyteIzmenitParametryPoiska")}
        </p>
      </>
    ) : (
      <>
        <h3 className="text-xl font-semibold mb-2">
          {tUi("tournamentListOptimized.zhdemPokaKtoNibud")}
        </h3>
        <p className="mx-auto mb-5 max-w-md text-muted-foreground">
          {tUi("tournamentListOptimized.hotiteProvestiSvoyEto")}
        </p>
        <Button asChild>
          <Link href="/create-tournament">{tUi("tournamentListOptimized.provestiTurnir")}</Link>
        </Button>
      </>
    )}
  </div>
  );
});
EmptyState.displayName = "EmptyState";

const LoadingSkeleton = memo(({ viewMode }: { viewMode: ViewMode }) => {
  if (viewMode === "grid") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <TournamentCardSkeletonMemo key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <TournamentCardSkeletonMemo key={index} />
      ))}
    </div>
  );
});
LoadingSkeleton.displayName = "LoadingSkeleton";

const TournamentGrid = memo(
  ({
    tournaments,
    tournamentFriendsMap,
  }: {
    tournaments: Tournament[];
    tournamentFriendsMap?: Map<string, { friends: FriendListItem[]; count: number }>;
  }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
      {tournaments.map((tournament) => (
        <TournamentCardMemo
          key={tournament.id}
          tournament={tournament}
          friendsInTournament={tournamentFriendsMap?.get(String(tournament.id))}
        />
      ))}
    </div>
  ),
);
TournamentGrid.displayName = "TournamentGrid";

const TournamentList = memo(
  ({ tournaments }: { tournaments: Tournament[] }) => (
    <div className="flex flex-col gap-4">
      {tournaments.map((tournament) => (
        <TournamentInfoRowMemo key={tournament.id} tournament={tournament} />
      ))}
    </div>
  ),
);
TournamentList.displayName = "TournamentList";

const LoadMoreTrigger = memo(
  ({ onLoadMore, hasMore }: { onLoadMore?: () => void; hasMore?: boolean }) => {
    const { ref, isIntersecting } = useIntersectionObserver({
      threshold: 0.1,
      rootMargin: "100px",
    });

    const didTriggerRef = useRef(false);

    useEffect(() => {
      if (!isIntersecting) {
        didTriggerRef.current = false;
        return;
      }
      if (!hasMore || !onLoadMore) return;
      if (didTriggerRef.current) return;
      didTriggerRef.current = true;
      onLoadMore();
    }, [isIntersecting, hasMore, onLoadMore]);

    return <div ref={ref} className="h-4" />;
  },
);
LoadMoreTrigger.displayName = "LoadMoreTrigger";

export const TournamentListOptimized = ({
  tournaments,
  isLoading,
  viewMode,
  onLoadMore,
  hasMore,
  tournamentFriendsMap,
  filtered,
}: TournamentListProps) => {
  const isMobile = useIsMobile();

  const optimizedViewMode = useMemo(() => {
    if (isMobile) return "list";
    return viewMode;
  }, [viewMode, isMobile]);

  if (isLoading) {
    return <LoadingSkeleton viewMode={optimizedViewMode} />;
  }

  if (tournaments.length === 0) {
    return <EmptyState filtered={filtered} />;
  }

  return (
    <div className="space-y-5">
      {optimizedViewMode === "list" ? (
        <TournamentList tournaments={tournaments} />
      ) : (
        <TournamentGrid
          tournaments={tournaments}
          tournamentFriendsMap={tournamentFriendsMap}
        />
      )}

      {onLoadMore && (
        <div className="mt-2 flex justify-center">
          <LoadMoreTrigger onLoadMore={onLoadMore} hasMore={hasMore ?? false} />
        </div>
      )}
    </div>
  );
};
TournamentListOptimized.displayName = "TournamentListOptimized";

export default withErrorBoundary(TournamentListOptimized);
